"use client"

import type React from "react"
import { useEffect, useRef } from "react"
import { mouseStore } from "../mouseStore"

interface CursorLensProps {
  /** Lens circle diameter in px (default 140) */
  size?: number
  /** Zoom factor — how much the content is enlarged inside the lens (default 2.2) */
  scale?: number
  /** Border of the circular lens frame */
  border?: string
  /** z-index of the lens overlay (default 9995) */
  zIndex?: number
  /** Content to wrap. The same content renders twice: once normally, once zoomed inside the lens. */
  children: React.ReactNode
}

/**
 * Wraps children and shows a circular magnifying-glass lens that follows the
 * cursor over the wrapped area. The lens renders a true zoomed copy of the
 * children via DOM duplication + CSS scale — no canvas, no screenshots.
 *
 * @example
 * <CursorLens scale={2.5}>
 *   <img src="..." alt="..." />
 * </CursorLens>
 */
export const CursorLens: React.FC<CursorLensProps> = ({
  size = 140,
  scale = 2.2,
  border = "1.5px solid rgba(255,255,255,0.25)",
  zIndex = 9995,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const lensRef = useRef<HTMLDivElement>(null)
  const zoomedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    const lens = lensRef.current
    const zoomed = zoomedRef.current
    if (!container || !lens || !zoomed) return

    const unsub = mouseStore.subscribe((x, y) => {
      const rect = container.getBoundingClientRect()

      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        lens.style.opacity = "0"
        return
      }

      const lx = x - rect.left
      const ly = y - rect.top

      lens.style.left = `${x}px`
      lens.style.top = `${y}px`
      lens.style.opacity = "1"

      zoomed.style.width = `${rect.width}px`
      zoomed.style.height = `${rect.height}px`

      // Offset so cursor point aligns to lens centre: left = size/2 - lx*s
      zoomed.style.left = `${size / 2 - lx * scale}px`
      zoomed.style.top  = `${size / 2 - ly * scale}px`
    })

    const onLeave = () => { lens.style.opacity = "0" }
    document.addEventListener("mouseleave", onLeave)

    return () => {
      unsub()
      document.removeEventListener("mouseleave", onLeave)
    }
  }, [size, scale])

  return (
    <>
      {/* ── Base layer — renders normally ─────────────────────────────── */}
      <div ref={containerRef} style={{ position: "relative" }}>
        {children}
      </div>

      {/* ── Lens overlay — fixed, follows cursor ──────────────────────── */}
      <div
        ref={lensRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          width: size,
          height: size,
          borderRadius: "50%",
          border,
          boxShadow:
            "0 0 0 1px rgba(255,255,255,0.06) inset, 0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)",
          overflow: "hidden",
          pointerEvents: "none",
          zIndex,
          opacity: 0,
          transform: "translate(-50%, -50%)",
          transition: "opacity 0.14s ease",
        }}
      >
        {/* Zoomed copy — same children, scaled and offset to keep cursor centred */}
        <div
          ref={zoomedRef}
          aria-hidden="true"
          style={{
            position: "absolute",
            transformOrigin: "0 0",
            transform: `scale(${scale})`,
            pointerEvents: "none",
          }}
        >
          {children}
        </div>

        {/* Glass overlay: subtle inner highlight ring */}
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          background:
            "radial-gradient(ellipse at 35% 30%, rgba(255,255,255,0.07) 0%, transparent 60%)",
          pointerEvents: "none",
        }} />

        {/* Crosshairs */}
        <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.12)", transform: "translateY(-0.5px)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: "rgba(255,255,255,0.12)", transform: "translateX(-0.5px)", pointerEvents: "none" }} />

        {/* Scale label */}
        <div style={{
          position: "absolute", bottom: 8, right: 10,
          fontSize: 9, fontFamily: "monospace",
          color: "rgba(255,255,255,0.35)",
          letterSpacing: "0.08em",
          pointerEvents: "none",
        }}>
          {scale}×
        </div>
      </div>
    </>
  )
}
